'use client';

import React, { Component, ErrorInfo, ReactNode } from 'react';
import appLogger from '@/utils/logger';
import { handleDeploymentErrorReload } from '@/utils/deploymentErrorRecovery';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    appLogger.trackError(error, {
      component: 'ErrorBoundary',
      message: 'React error boundary captured error',
      source: 'react-error-boundary',
      component_stack: errorInfo.componentStack,
      error_boundary_name: this.constructor.name,
    });

    // Call the optional error handler
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // Auto-reload for deployment-related errors (with loop protection, and deferred rather
    // than immediate if a chat send is in-flight -- see deploymentErrorRecovery.ts).
    handleDeploymentErrorReload(error.message || '');
  }

  public render() {
    if (this.state.hasError) {
      // Return custom fallback UI or default error message
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className='flex flex-col items-center justify-center p-8 bg-red-50 border border-red-200 rounded-lg'>
          <div className='text-center'>
            <svg
              className='w-12 h-12 mx-auto text-red-400 mb-4'
              fill='currentColor'
              viewBox='0 0 24 24'
            >
              <path d='M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z' />
            </svg>
            <h3 className='text-lg font-medium text-red-800 mb-2'>
              Something went wrong
            </h3>
            <p className='text-red-600 mb-4'>
              We&apos;re sorry, but this section couldn&apos;t load properly.
            </p>
            <button
              onClick={() => this.setState({ hasError: false, error: null })}
              className='px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-200'
            >
              Try again
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
